/**
 * Property Prediction Action Definitions
 *
 * Actions for predicting ADMET properties, GNINA binding scores,
 * and computing physicochemical properties via the search service.
 *
 * @module gibbs/registry/actions/property-pred
 */

import { WorkflowTypes as T } from '../types';
import { param, pre } from '../schema';

// ─── Predict ADMET Properties ───────────────────────────────────────────────

export const predictAdmet = {
    id: 'predict-admet',
    name: 'Predict ADMET Properties',
    category: 'Property Prediction',
    description: 'Compute the 22 canonical ADMET (Absorption, Distribution, Metabolism, Excretion, Toxicity) property predictions currently shipped by MapLight. Properties are applied in the maplight scope on compound objects. See job-maplight for the full property list.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds with SMILES'),
    ],
    outputs: [
        param('predictions', T.PropertySet, '22 canonical ADMET predictions per compound'),
    ],
    parameters: [
        param('recalculate', 'boolean', 'Force recalculation', false, { defaultValue: false }),
        param('includeProps', 'object', 'Only compute these properties (string[])', false),
        param('excludeProps', 'object', 'Skip these properties (string[])', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compounds must be loaded with SMILES'),
        pre('internet_available', 'MapLight service must be reachable'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'UpdateCompoundExternalProps',
    serverCommand: null,
    jobType: null,
};

// ─── Predict GNINA Binding Scores ───────────────────────────────────────────

export const predictGninaScores = {
    id: 'predict-gnina-scores',
    name: 'Predict GNINA Binding Scores',
    category: 'Property Prediction',
    description: 'Score compounds using GNINA\'s CNN-based model. Returns affinity and CNN confidence scores. Properties applied in gnina_scoring scope: {affinity, cnn_score, cnn_affinity, cnn_vs, cnn_variance} per compound. By default the scoring step re-docks internally to generate pose candidates; set `skipDocking: true` when the compounds have already been docked and you only want to score the existing poses.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds with 3D coordinates'),
    ],
    outputs: [
        param('scores', T.PropertySet, '{affinity, cnn_score, cnn_affinity, cnn_vs, cnn_variance} per compound'),
    ],
    parameters: [
        param('skipDocking', 'boolean', 'Skip internal re-docking and score existing poses as-is. Set to true when used after an upstream dock-compound step in the same workflow.', false, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'Compounds must be loaded'),
        pre('compound_has_3d', 'Compounds must have 3D coordinates'),
    ],
    postconditions: [],
    // GNINA CNN rescoring is a GPU-heavy server-side operation, not a
    // quick BFD round-trip. Reclassified from 'short' to 'long' after
    // the original 30s / bumped 60s timeouts both fired on in-progress
    // scoring jobs while the server was still computing.
    runtime: 'long',
    status: 'implemented',
    userAction: 'ScoreCompounds',
    serverCommand: null,
    jobType: null,
};

// ─── Compute Physicochemical Properties ─────────────────────────────────────

export const computeSearchProperties = {
    id: 'compute-search-properties',
    name: 'Compute Physicochemical Properties (Search Service)',
    category: 'Property Prediction',
    description: 'Calculate basic physicochemical properties for molecules via the search service. Available properties: HAC, TPSA, LogP, HBA, HBD, charge, rotatable bond count (RBC), molecular weight (MW), SMILES.',
    inputs: [
        param('smiles', T.SMILES, 'Molecule SMILES string', false),
        param('fragment', 'string', 'Fragment identifier', false),
    ],
    outputs: [
        param('properties', T.PropertySet, '{HAC, TPSA, LogP, HBA, HBD, charge, RBC, MW, smiles}'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: null,
    serverCommand: 'search-properties',
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    predictAdmet,
    predictGninaScores,
    computeSearchProperties,
];
